import { SiteUrls } from '@tableau/shared-urls';
import React from 'react';
import { Redirect, Route, RouteComponentProps, Switch } from 'react-router-dom';

import { Messages } from '../../../../../gen-src/Localize';
import { FlowQuery } from '../../../../../gen-src/queries';
import {
  CenteredMessage,
  FeatureFlagsContext,
  messageWithOrWithoutCount,
  SelectionProviderChildProps,
  SiteUrlsContext,
} from '../../../data-catalog-shared';
import { BrowserTitleUpdater, BrowserTitleUpdateType } from '../BrowserTitleUpdater';
import { ErrorListProvider } from '../error/components/ErrorListProvider';
import { ErrorToasts } from '../error/components/ErrorToasts';
import { UnrecognizedUrl } from '../error/components/UnrecognizedUrl';
import { LineageItemType } from '../lineage/LineageItemType';
import { LineageItemConfig, lineageItemKey, LineageSection, LineageSectionType } from '../placePage/LineageConfiguration';
import { FlowLineageSubplaceLayout } from '../placePage/LineageSubplaceLayout';
import { FlowFieldDetailsDialog } from './FlowFieldDetailsDialog';
import { FlowLineagePaneParams, memoizedGetFlowLineageConfiguration } from './FlowLineageConfiguration';
import { FlowLineageCounts, FlowLineageCountsQueryWrapper } from './FlowLineageCountsQueryWrapper';
import { FlowQueryWrapper } from './FlowQueryWrapper';
import { FlowSubplaceParamsProvider } from './FlowSubplaceParamsProvider';

type FlowQuery_flows = Unpacked<FlowQuery['flows']>
type FlowQuery_flows_outputSteps = Unpacked<FlowQuery_flows['outputSteps']>

export interface FlowLineageSubplaceRouterProps {
  readonly flowVizportalUrlId: string;
  readonly shouldShowFlowParametersWarning?: boolean;
}

export class FlowLineageSubplaceRouter extends React.Component<FlowLineageSubplaceRouterProps> {

  public override render(): JSX.Element {
    return (
      <SiteUrlsContext.Consumer>
        {urls =>
          <Route
            path={urls.flowLineagePath(this.props.flowVizportalUrlId)}
            render={() => this.renderFlowLineageRoute(urls)}
          />
        }
      </SiteUrlsContext.Consumer>
    );
  }

  private readonly renderFlowLineageRoute = (urls: SiteUrls): JSX.Element => {
    return (
      <FeatureFlagsContext.Consumer>
        {
          featureFlags =>
            <Switch>
              {memoizedGetFlowLineageConfiguration(
                featureFlags.metricsDataCatalogEnabled,
                featureFlags.publishedConnectionsLineageEnabled,
                featureFlags.lensLineageEnabled).map(lineageSection =>
                  lineageSection.items.map((lineageItem, i) =>
                    <Route
                      key={i}
                      path={lineageItem.path(urls, this.props.flowVizportalUrlId)}
                      render={() => this.renderLineageItemRoute(urls, lineageItem, lineageSection)}
                    />
                  )
                )}
              <Redirect
                exact={true}
                from={urls.flowLineagePath(this.props.flowVizportalUrlId)}
                to={urls.flowLineageFieldsPath(this.props.flowVizportalUrlId)}
              />
              <UnrecognizedUrl
                backToButtonLabel={Messages.BackToFields()}
                backToButtonUrl={urls.flowLineageFieldsPath(this.props.flowVizportalUrlId)}
              />
            </Switch>
        }
      </FeatureFlagsContext.Consumer>
    );
  }

  private getFieldsCountForSelectedOutputStep(
    flow: FlowQuery_flows,
    selectedOutputStepId: string,
  ): number | null {
    const outputStep = flow.outputSteps.find(s => s.id === selectedOutputStepId);
    if (outputStep && outputStep.outputFieldsConnection) {
      return outputStep.outputFieldsConnection.totalCount;
    } else {
      return null;
    }
  }

  private checkForLineageError(flow: FlowQuery_flows): boolean {
    // Can't generate complete lineage if the output DS/DB has not been published.
    // Published DS/DB need to be ingested to populate columns/fields correctly.
    if ((flow.downstreamDatasourcesConnection == null || flow.downstreamDatasourcesConnection.totalCount === 0) &&
      (flow.downstreamDatabasesConnection == null || flow.downstreamDatabasesConnection.totalCount === 0)) {
      return true;
    }

    // Can't generate lineage for a flow that has not be successfully run
    if (flow.outputSteps.length === 0) {
      return true;
    }

    return false;
  }

  private readonly renderLineageItemRoute = (
    urls: SiteUrls,
    lineageItem: LineageItemConfig<FlowLineageCounts, FlowLineagePaneParams>,
    lineageSection: LineageSection<FlowLineageCounts, FlowLineagePaneParams>,
  ): JSX.Element => {
    return (
      <FlowSubplaceParamsProvider>
        {params =>
          <ErrorListProvider>
            <ErrorToasts />
            <FlowQueryWrapper flowVizportalUrlId={this.props.flowVizportalUrlId}>
              {flow => {

                if (this.checkForLineageError(flow)){
                  return <CenteredMessage message={Messages.EmptyFlowGridErrorMessage()} />;
                }

                const selectedOutputStepId = params.selectedOutputStepId || flow.outputSteps[0].id;
                const fieldsCountForSelectedOutputStep =
                  this.getFieldsCountForSelectedOutputStep(flow, selectedOutputStepId);

                return (
                  <FlowLineageCountsQueryWrapper
                    flowVizportalUrlId={this.props.flowVizportalUrlId}
                    selectedOutputStepId={selectedOutputStepId}
                    selectedKeys={params.selectedFlowOutputFieldIds}
                  >
                    {({ countsLoading, unfilteredCounts, filteredCounts }) =>
                      <>
                        <FlowLineageSubplace
                          flowVizportalUrlId={this.props.flowVizportalUrlId}
                          flow={flow}
                          lineageItem={lineageItem}
                          lineageSection={lineageSection}
                          countsLoading={countsLoading}
                          unfilteredCounts={unfilteredCounts}
                          filteredCounts={filteredCounts}
                          urlSearchValue={params.urlSearchValue}
                          clearFieldSelection={params.clearFlowOutputFieldSelection}
                          selectedKeys={params.selectedFlowOutputFieldIds}
                          updateSelectionForKey={params.updateFlowOutputFieldSelection}
                          selectedOutputStepId={params.selectedOutputStepId || flow.outputSteps[0].id}
                          updateOutputStepSelection={params.updateOutputStepSelection}
                          fieldsCountForSelectedOutputStep={fieldsCountForSelectedOutputStep}
                          outputSteps={flow.outputSteps}
                          shouldShowFlowParametersWarning={this.props.shouldShowFlowParametersWarning}
                        />
                        {lineageSection.sectionType === LineageSectionType.Anchor ?
                          <Route
                            path={urls.flowLineageFieldPath(this.props.flowVizportalUrlId, ':flowFieldId')}
                            render={({ history, location, match }: RouteComponentProps<{ flowFieldId: string }>) =>
                              <FlowFieldDetailsDialog
                                flowOutputStepId={params.selectedOutputStepId || flow.outputSteps[0].id}
                                flowFieldId={match.params.flowFieldId}
                                onClose={() => history.replace({
                                  ...location,
                                  pathname: urls.flowLineageFieldsPath(this.props.flowVizportalUrlId)
                                })}
                              />
                            }
                          />
                          : null
                        }
                      </>
                    }
                  </FlowLineageCountsQueryWrapper>
                );
              }}
            </FlowQueryWrapper>
          </ErrorListProvider>
        }
      </FlowSubplaceParamsProvider>
    );
  }
}


type FlowLineageSubplaceProps = {
  readonly flowVizportalUrlId: string;
  readonly flow: FlowQuery_flows;
  readonly lineageItem: LineageItemConfig<FlowLineageCounts, FlowLineagePaneParams>;
  readonly lineageSection: LineageSection<FlowLineageCounts, FlowLineagePaneParams>;
  readonly countsLoading: boolean;
  readonly unfilteredCounts: FlowLineageCounts | null;
  readonly filteredCounts: FlowLineageCounts | null;
  readonly urlSearchValue: string;
  readonly clearFieldSelection: () => void;
  readonly selectedOutputStepId: string;
  readonly updateOutputStepSelection: (outputStepId: string) => void;
  readonly fieldsCountForSelectedOutputStep: number | null;
  readonly outputSteps: ReadonlyArray<FlowQuery_flows_outputSteps>;
  readonly shouldShowFlowParametersWarning?: boolean;
} & SelectionProviderChildProps;

class FlowLineageSubplace extends React.PureComponent<FlowLineageSubplaceProps> {

  public override render(): JSX.Element {
    const selectedFlowOutputFieldIds = Object.keys(this.props.selectedKeys);
    const hasFilter = selectedFlowOutputFieldIds.length > 0;
    const filteredFlowOutputFieldId: string | null = hasFilter ? selectedFlowOutputFieldIds[0] : null;
    const lineageCounts = this.props.filteredCounts ? this.props.filteredCounts : this.props.unfilteredCounts;
    return (
      <>
        <BrowserTitleUpdater
          name={this.props.flow.name}
          browserTitleUpdateType={BrowserTitleUpdateType.TITLE_WITH_ENTITY_NAME_AND_SUBSPACE_NAME}
          subspaceName={Messages.LineageNavHeader()}
        />
        <FlowLineageSubplaceLayout
          activeLineageItemKey={lineageItemKey(this.props)}
          selectedAnchorItemIds={selectedFlowOutputFieldIds}
          pane={this.props.lineageItem.paneFactory({
            title: messageWithOrWithoutCount(
              lineageCounts && this.props.lineageItem.count(lineageCounts),
              this.props.lineageItem.messagesForPane
            ),
            selectedKeys: this.props.selectedKeys,
            updateSelectionForKey: this.props.updateSelectionForKey,
            flowVizportalUrlId: this.props.flowVizportalUrlId,
            filteredFlowOutputFieldId: filteredFlowOutputFieldId,
            count: lineageCounts && this.props.lineageItem.count(lineageCounts),
            selectedOutputStepId: this.props.selectedOutputStepId,
            updateOutputStepSelection: this.props.updateOutputStepSelection,
            fieldsCountForSelectedOutputStep: this.props.fieldsCountForSelectedOutputStep,
            outputSteps: this.props.outputSteps,
            shouldShowFlowParametersWarning: this.props.shouldShowFlowParametersWarning,
            flowLuid: this.props.flow.luid,
          })}
          flowVizportalUrlId={this.props.flowVizportalUrlId}
          countsLoading={this.props.countsLoading}
          unfilteredCounts={this.props.unfilteredCounts}
          filteredCounts={this.props.filteredCounts}
          urlSearchValue={this.props.urlSearchValue}
          clearFieldSelection={this.props.clearFieldSelection}
          anchorName={this.props.flow.name}
          anchorCount={this.props.outputSteps.length}
          filteredOnItemType={hasFilter ? LineageItemType.Flow : null}
          filteredItemName={this.props.filteredCounts?.filteredFieldName ?? null}
          lineageSectionType={this.props.lineageSection.sectionType}
          shouldShowFlowParametersWarning={this.props.shouldShowFlowParametersWarning}
        />
      </>
    );
  }

}
